import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useAuth } from '../../../firebase/AuthProvider';
import {
    ServiceTypeDto,
    ServiceFrequencyTypeDto,
    ServiceUnitTypeDto,
    ServiceDefinitionDto,
    UpdateServiceDefinitionReq,
} from '../../../interfaces/serviceDefinition/service-definition';
import { GetAllServiceTypesAsync } from '../../../services/ServiceDefinition/ServiceTypeService';
import { GetAllServiceFrequencyTypesAsync } from '../../../services/ServiceDefinition/ServiceFrequencyTypeService';
import { GetAllServiceUnitTypeAsync } from '../../../services/ServiceDefinition/ServiceUnitTypeService';
import { GetServiceDefinitionByIdAsync, UpdateServiceDefinitionAsync } from '../../../services/ServiceDefinition/ServiceDefinitionService';
import { AxiosError } from 'axios';
import Loader from '../loader/Loader';


const EditParticipantServiceDefinition = () => {
    const { serviceId } = useParams<{ serviceId: string }>();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState<UpdateServiceDefinitionReq>({
        id: '',
        code: '',
        name: '',
        description: '',
        serviceTypeId: null,
        active: true,
        defaultFrequencyTypeId: null,
        defaultUnitTypeId: null,
        defaultPlannedUnits: null,
        metadata: '{}',
    });

    const [errors, setErrors] = useState<any>({});
    const [serviceTypes, setServiceTypes] = useState<ServiceTypeDto[]>([]);
    const [frequencyTypes, setFrequencyTypes] = useState<ServiceFrequencyTypeDto[]>([]);
    const [unitTypes, setUnitTypes] = useState<ServiceUnitTypeDto[]>([]);
    const [loading, setLoading] = useState({
        loadingServiceTypes: false,
        loadingFrequencyTypes: false,
        loadingUnitTypes: false,
        loadingService: false,
        submitting: false,
    });

    useEffect(() => {
        getServiceDefinitionById();
        GetData();
        // eslint-disable-next-line
    }, [serviceId]);

    const GetData = async () => {
        const token = await currentUser?.getIdToken();
        if (!token || !serviceId) return;
        setLoading(prev => ({
            ...prev,
            loadingServiceTypes: true,
            loadingFrequencyTypes: true,
            loadingUnitTypes: true,
        }));

        try {
            // Fetch all dropdown data in parallel
            await Promise.all([
                GetAllServiceTypesAsync(token).then(res => {
                    setServiceTypes(res.data || []);
                    setLoading(prev => ({ ...prev, loadingServiceTypes: false }));
                }),
                GetAllServiceFrequencyTypesAsync(token).then(res => {
                    setFrequencyTypes(res.data || []);
                    setLoading(prev => ({ ...prev, loadingFrequencyTypes: false }));
                }),
                GetAllServiceUnitTypeAsync(token).then(res => {
                    setUnitTypes(res.data || []);
                    setLoading(prev => ({ ...prev, loadingUnitTypes: false }));
                }),
            ]);
            // Fetch the service definition to edit

        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Loading Data");
        }
    };

    const getServiceDefinitionById = async () => {
        const token = await currentUser?.getIdToken();
        if (!token || !serviceId) return;
        try {
            setLoading(prev => ({ ...prev, loadingService: true }));
            const res = await GetServiceDefinitionByIdAsync(token, serviceId);
            if (res.success && res.data) {
                setForm({
                    id: res.data.id,
                    code: res.data.code,
                    name: res.data.name,
                    description: res.data.description,
                    serviceTypeId: res.data.serviceTypeId,
                    active: res.data.active,
                    defaultFrequencyTypeId: res.data.defaultFrequencyTypeId,
                    defaultUnitTypeId: res.data.defaultUnitTypeId,
                    defaultPlannedUnits: res.data.defaultPlannedUnits,
                    metadata: res.data.metadata,
                });
            } else {
                toast.error(res.message || 'Failed to load service definition');
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Loading Service");
        } finally { setLoading(prev => ({ ...prev, loadingService: false })); }
    }

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: undefined });
    };

    const handleSelectChange = (
        name: string,
        selected: { value: string; label: string } | null
    ) => {
        setForm({ ...form, [name]: selected ? selected.value : null });
        setErrors({ ...errors, [name]: undefined });
    };

    const validate = () => {
        const e: any = {};
        if (!form.code) e.code = 'Code is required.';
        if (!form.name) e.name = 'Name is required.';
        if (!form.description) e.description = 'Description is required.';
        if (!form.serviceTypeId) e.serviceTypeId = 'Service Type is required.';
        if (!form.defaultFrequencyTypeId) e.defaultFrequencyTypeId = 'Frequency Type is required.';
        if (!form.defaultUnitTypeId) e.defaultUnitTypeId = 'Unit Type is required.';

        if (
            form.defaultPlannedUnits === undefined ||
            form.defaultPlannedUnits === null ||
            form.defaultPlannedUnits === 0
        ) {
            e.defaultPlannedUnits = 'Planned Units is required.';
        } else if (Number(form.defaultPlannedUnits) < 0) {
            e.defaultPlannedUnits = 'Planned Units must be non-negative.';
        }

        return e;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const v = validate();
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        const token = await currentUser?.getIdToken();
        if (!token) return;


        const payload: UpdateServiceDefinitionReq = {
            ...form,
            defaultPlannedUnits: form.defaultPlannedUnits
                ? Number(form.defaultPlannedUnits)
                : null,
            metadata: form.metadata || '{}',
        };

        try {
            setLoading(prev => ({ ...prev, submitting: true }));
            const res = await UpdateServiceDefinitionAsync(token, payload);
            if (res.success) {
                toast.success(res.message);
                navigate('/dashboard/owner/service-definition');
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || 'Error updating the Service Definition');
        } finally { setLoading(prev => ({ ...prev, submitting: false })); }
    };

    const serviceTypeOptions = serviceTypes.map(t => ({
        value: t.id,
        label: t.name,
    }));
    const frequencyTypeOptions = frequencyTypes.map(t => ({
        value: t.id,
        label: t.name,
    }));
    const unitTypeOptions = unitTypes.map(t => ({
        value: t.id,
        label: t.name,
    }));

    return (
        <>
            <div className="common_card">
                {loading.loadingService && <Loader />}
                <div className="top top_bar_flx">
                    <div className="top_bar_tittle">
                        <h2>Edit Participant Service Definition</h2>
                    </div>
                </div>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    name="code"
                                    value={form.code}
                                    onChange={handleChange}
                                    isInvalid={!!errors.code}
                                    disabled={loading.loadingService}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.code}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    disabled={loading.loadingService}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    isInvalid={!!errors.description}
                                    disabled={loading.loadingService}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.description}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Service Type</Form.Label>
                                <Select
                                    name="serviceTypeId"
                                    value={serviceTypeOptions.find(
                                        o => o.value === form.serviceTypeId
                                    )}
                                    onChange={selected =>
                                        handleSelectChange('serviceTypeId', selected)
                                    }
                                    options={serviceTypeOptions}
                                    isDisabled={loading.loadingServiceTypes}
                                />
                                {errors.serviceTypeId && (
                                    <div className="invalid-feedback d-block">
                                        {errors.serviceTypeId}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Frequency Type</Form.Label>
                                <Select
                                    name="defaultFrequencyTypeId"
                                    value={frequencyTypeOptions.find(
                                        o => o.value === form.defaultFrequencyTypeId
                                    )}
                                    onChange={selected =>
                                        handleSelectChange('defaultFrequencyTypeId', selected)
                                    }
                                    options={frequencyTypeOptions}
                                    isDisabled={loading.loadingFrequencyTypes}
                                />
                                {errors.defaultFrequencyTypeId && (
                                    <div className="invalid-feedback d-block">
                                        {errors.defaultFrequencyTypeId}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Unit Type</Form.Label>
                                <Select
                                    name="defaultUnitTypeId"
                                    value={unitTypeOptions.find(
                                        o => o.value === form.defaultUnitTypeId
                                    )}
                                    onChange={selected =>
                                        handleSelectChange('defaultUnitTypeId', selected)
                                    }
                                    options={unitTypeOptions}
                                    isDisabled={loading.loadingUnitTypes}
                                />
                                {errors.defaultUnitTypeId && (
                                    <div className="invalid-feedback d-block">
                                        {errors.defaultUnitTypeId}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Planned Units</Form.Label>
                                <Form.Control
                                    name="defaultPlannedUnits"
                                    type="number"
                                    value={form.defaultPlannedUnits ?? ''}
                                    onChange={handleChange}
                                    isInvalid={!!errors.defaultPlannedUnits}
                                    disabled={loading.loadingService}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.defaultPlannedUnits}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>Metadata (JSON)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="metadata"
                                    value={form.metadata}
                                    onChange={handleChange}
                                    disabled={loading.loadingService}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="form_btn_flx mby_t_24">
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={loading.submitting || loading.loadingService}
                        >
                            {loading.submitting ? <span><Spinner size='sm' /> Updating...</span> : 'Update Service'}
                        </Button>
                    </div>
                </Form>
            </div>
        </>
    );
};

export default EditParticipantServiceDefinition;